import { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { api } from "../lib/api";
import { useWorld } from "../state/World";

/**
 * Proves MelzAI isn't scripted — within a guardrail.
 *
 * This is an open alpha, so each surface gets a small number of genuine Claude
 * calls per person and then quietly returns to pre-vetted Demo Mode. Live is
 * demonstrated in every Space, across every Place, in every Practice, rather
 * than spent all in one room. The toggle says how many swings are left so
 * nobody discovers the limit by hitting it.
 */
export const LiveToggle = ({ accent = "gold", surface }) => {
  const { live, setLive, me } = useWorld();
  const [info, setInfo] = useState(null);
  const available = me?.melzai_live_available;

  useEffect(() => {
    if (!available) return;
    const pull = () =>
      api
        .get("/melzai/live-state")
        .then(({ data }) => setInfo(data))
        .catch(() => {});
    pull();
    window.addEventListener("melz-live-used", pull);
    return () => window.removeEventListener("melz-live-used", pull);
  }, [available]);

  const here = surface ? info?.surfaces?.[surface] : null;
  const spent = Boolean(here?.spent);
  const on = live && available && !spent;
  const color = accent === "cyan" ? "var(--arcade-cyan)" : "var(--stage-gold)";

  const label = !available
    ? "Demo Mode"
    : spent
    ? "Live proven · Demo Mode"
    : on
    ? `Live · Claude${here ? ` · ${here.left} left` : ""}`
    : "Demo Mode";

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="melzai-live-toggle"
        disabled={!available || spent}
        onClick={() => setLive(!live)}
        title={
          !available
            ? "No key configured — Demo Mode only"
            : spent
            ? "Live already proven on this surface. Open alpha: back to Demo Mode."
            : "Fire a real Claude call"
        }
        className="group flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors duration-300"
        style={{
          borderColor: on ? color : "rgba(255,255,255,0.14)",
          background: on ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.3)",
          cursor: available && !spent ? "pointer" : "not-allowed",
          opacity: available ? 1 : 0.5,
        }}
      >
        <Zap size={11} strokeWidth={1.6} style={{ color: on ? color : "rgba(255,255,255,0.35)" }} />
        <span
          className="tracked whitespace-nowrap text-[9px]"
          style={{ color: on ? color : "rgba(255,255,255,0.45)" }}
          data-testid="melzai-mode-label"
        >
          {label}
        </span>
      </button>
    </div>
  );
};

export default LiveToggle;
